import React, { useCallback, useMemo } from "react";
import { SectionSelectItem } from "./SectionSelectItem";
import { Connection, Section, Task } from "../State/Routine/types";
import { NumberGoalValueInput } from "../../../components/mobile/NumberGoalValueInput";
import { AHSeparator, Box } from "arkh";

import { RoutineEditMainOrgTopicSelector } from "../RoutineEditMain/RoutineEditMainOrgTopicListItem";
import { graphql, useFragment } from "react-relay";
import { TaskConnections_topics$key } from "./__generated__/TaskConnections_topics.graphql";
import { RoutineEditMainSectionSelectableSelect } from "../RoutineEditMain/RoutineEditMainSectionSelectableSelect";

type Props = {
  isOrg: boolean;
  task: Task;
  setConnections: (taskConnections: Connection[]) => void;
  connections: Connection[];
  sections: Section[];
  topics: TaskConnections_topics$key | null;
};

export function TaskConnections({
  isOrg,
  setConnections,
  connections,
  task,
  sections,
  topics: topicsKey,
}: Props) {
  const topics = useFragment(
    graphql`
      fragment TaskConnections_topics on OrgTopic @relay(plural: true) {
        id
        name
        isPrivate
        ...RoutineEditMainOrgTopicListItemSelector_topics
      }
    `,
    topicsKey
  );

  const onPressSection = useCallback(
    (sectionId: Connection["sectionId"], selected: boolean) => {
      if (selected) {
        setConnections([
          ...connections,
          {
            taskId: task.id,
            sectionId,
            value: 0,
            ordinal: connections.length,
            orgTopic: null,
          },
        ]);
      } else {
        setConnections(
          connections.filter(
            (_connection) =>
              !(
                _connection.sectionId === sectionId &&
                _connection.taskId === task.id
              )
          )
        );
      }
    },
    [connections, setConnections, task.id]
  );

  const taskConnections = useMemo(() => {
    return connections.filter((c) => c.taskId === task.id);
  }, [connections, task.id]);

  return (
    <>
      {sections.map((section, index) => {
        const connection = taskConnections.find(
          ({ sectionId }) => sectionId === section.id
        );
        const isSectionSelected = !!connection;

        return (
          <React.Fragment key={section.id}>
            {index > 0 ? <AHSeparator /> : null}
            <SectionSelectItem
              isFirst={index === 0}
              isLast={index === sections.length - 1}
              key={section.id}
              selected={isSectionSelected}
              onChange={(isSelected) => onPressSection(section.id, isSelected)}
              section={section}
            >
              {task.type === "TaskNumber" && task.goal && connection ? (
                <NumberGoalValueInput
                  goal={task.goal}
                  value={connection.value}
                  setValue={(value) => {
                    setConnections(
                      connections.map((connection) =>
                        connection.sectionId === section.id &&
                        connection.taskId === task.id
                          ? {
                              ...connection,
                              value,
                            }
                          : connection
                      )
                    );
                  }}
                />
              ) : null}
              {isOrg && isSectionSelected ? (
                <>
                  <RoutineEditMainSectionSelectableSelect
                    canBeDisabled={
                      section.orgCanBeDisabled
                        ? true
                        : (connection?.canBeDisabled ?? false)
                    }
                    disabledSelector={section.orgCanBeDisabled}
                    onValueChange={(v) => {
                      setConnections(
                        connections.map((connection) =>
                          connection.sectionId === section.id &&
                          connection.taskId === task.id
                            ? { ...connection, canBeDisabled: v }
                            : connection
                        )
                      );
                    }}
                  />
                  {topics ? (
                    <Box spaceLeft="xsmall">
                      <RoutineEditMainOrgTopicSelector
                        topics={topics}
                        selectedTopic={connection?.orgTopic?.id ?? "NONE"}
                        onSelectedTopicChange={(topicId) => {
                          const topic = topics.find((t) => t.id === topicId);
                          setConnections(
                            connections.map((connection) =>
                              connection.sectionId === section.id &&
                              connection.taskId === task.id
                                ? {
                                    ...connection,
                                    orgTopic: topic
                                      ? {
                                          id: topic.id,
                                          name: topic.name,
                                          isPrivate: topic.isPrivate,
                                        }
                                      : null,
                                  }
                                : connection
                            )
                          );
                        }}
                      />
                    </Box>
                  ) : null}
                </>
              ) : null}
            </SectionSelectItem>
          </React.Fragment>
        );
      })}
    </>
  );
}
